import { workoutDisplayDigits as displayDigits } from '../../utils/workoutNumberFormat';
import { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';

export type PerformanceGuideKey = 'work' | 'variability' | 'ef' | 'ascent' | 'vam' | 'if' | 'normalizedPower' | 'pwhr' | 'wkg' | 'descent' | 'calories' | 'tss' | 'distance';

export type PerformanceGuideMetric = {
  key: PerformanceGuideKey;
  iconPath: string;
  color: string;
  label: string;
  val: string;
  unit?: string;
};

const PERFORMANCE_GUIDE_FORMULAS: Record<PerformanceGuideKey, string> = {
  work: 'Σ(Power × Δt) ÷ 1000',
  variability: 'VI = Normalized Power ÷ Average Power',
  ef: 'EF = Normalized Power ÷ Average Heart Rate',
  ascent: 'Σ positive elevation change',
  vam: 'VAM = Ascent (m) ÷ Time (h)',
  if: 'IF = Normalized Power ÷ FTP',
  normalizedPower: 'NP = ⁴√ mean((30 s rolling power)⁴)',
  pwhr: 'Pw:HR = Δ(Power ÷ Heart Rate) between halves (%)',
  wkg: 'W/kg = Power ÷ Body mass',
  descent: 'Σ absolute negative elevation change',
  tss: 'TSS = Duration (h) × (NP / FTP)² × 100',
  distance: 'Distance = Σ segment distance',
  calories: 'Energy expenditure ≈ metabolic cost of the activity',
};

export function PerformanceGuideModal({ metric, onClose }: { metric: PerformanceGuideMetric | null; onClose: () => void }) {
  const { t } = useTranslation();

  useEffect(() => {
    if (!metric) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        event.stopImmediatePropagation();
        onClose();
      }
    };
    window.addEventListener('keydown', onKeyDown, true);
    return () => window.removeEventListener('keydown', onKeyDown, true);
  }, [metric, onClose]);

  if (!metric || typeof document === 'undefined') return null;

  return createPortal(
    <div className="performance-metric-guide__overlay" onMouseDown={(event) => { if (event.target === event.currentTarget) onClose(); }}>
      <div className="performance-metric-guide" role="dialog" aria-modal="true" aria-labelledby="performance-metric-guide-title">
        <div className="performance-metric-guide__accent" style={{ background: metric.color }} />
        <button type="button" className="performance-metric-guide__close" onClick={onClose} aria-label={t('analysis.performanceGuide.close')}>×</button>
        <div className="performance-metric-guide__hero">
          <div className="performance-metric-guide__icon" style={{ color: metric.color, background: `${metric.color}18` }}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.1" strokeLinecap="round" strokeLinejoin="round"><path d={metric.iconPath}/></svg>
          </div>
          <div>
            <span className="performance-metric-guide__eyebrow">{t('analysis.performanceGuide.eyebrow')}</span>
            <h2 id="performance-metric-guide-title">{metric.label}</h2>
          </div>
          <div className="performance-metric-guide__value ltr-island">{displayDigits(metric.val)}<small>{metric.unit}</small></div>
        </div>
        <p className="performance-metric-guide__description">{t(`analysis.performanceGuide.metrics.${metric.key}.description`)}</p>
        <div className="performance-metric-guide__formula ltr-island">
          <span>{t('analysis.performanceGuide.formula')}</span>
          <code>{PERFORMANCE_GUIDE_FORMULAS[metric.key]}</code>
        </div>
        <div className="performance-metric-guide__insight">
          <span>{t('analysis.performanceGuide.interpretation')}</span>
          <p>{t(`analysis.performanceGuide.metrics.${metric.key}.interpretation`)}</p>
        </div>
      </div>
    </div>,
    document.body,
  );
}
